import React from 'react';
import PropTypes from 'prop-types';
import { Button } from 'antd';

import { intents, variants, sizes } from '../utils';
import TextButton from './TextButton';

/**
 * 
  * StyledButton is a wrapper around Ant Design's Button component.
  * It provides a consistent interface for different button types and styles.
  * 
  * @component
  * @param {Object} props - The properties passed to the component.
  * @param {string} props.variant - The variant of the button (primary, secondary, tertiary).
  * @param {string} props.intent - The intent of the button (default, primary, success, warning, danger).
  * @param {string} props.size - The size of the button (small, middle, large).
  * @param {ReactNode} props.glyph - The icon or glyph to be displayed in the button.
  * @param {boolean} props.disabled - Whether the button is disabled.
  * @param {boolean} props.busy - Whether the button is in a loading state.
  * @param {boolean} props.stretch - Whether the button should stretch to fill its container.
  * @param {string} props.link - The URL to navigate to when the button is clicked (for link variant).
  * @param {function} props.onClick - The function to call when the button is clicked.
  * @param {ReactNode} props.children - The content to be displayed inside the button.

 */

const StyledButton = (props) => {
  const {
    variant,
    intent,
    size,
    glyph,
    disabled,
    busy,
    stretch,
    link,
    onClick,
    children,
    ...rest
  } = props;

  if (variant === variants.text) {
    return (
      <TextButton
        intent={intent}
        text={children}
        link={link}
        onClick={onClick}
        disabled={disabled}
        busy={busy}
        {...rest}
      />
    );
  }

  return (
    <Button
      type={variant}
      danger={intent === 'danger'}
      size={size}
      icon={glyph}
      disabled={disabled}
      loading={busy}
      block={stretch}
      href={link}
      onClick={onClick}
      {...rest}
    >
      {children}
    </Button>
  );
};

StyledButton.propTypes = {
  variant: PropTypes.oneOf(Object.values(variants)),
  intent: PropTypes.oneOf(Object.values(intents)),
  size: PropTypes.oneOf(Object.values(sizes)),
  glyph: PropTypes.node,
  disabled: PropTypes.bool,
  busy: PropTypes.bool,
  stretch: PropTypes.bool,
  link: PropTypes.string,
  onClick: PropTypes.func,
  children: PropTypes.node,
};

export default StyledButton;
